import type { Game, PlaygameMessage } from '@playgame/types'
import type { GameProviderSlug } from '@game/types'
import { useState, createSharedComposable, useCookie, usePlaygameView, computed, useGameType } from '#imports'
import ProviderService from '@playgame/utils/ProviderService'

export const usePlaygameState = createSharedComposable(() => {
    const soundCookie = useCookie<boolean>('soundCookie')
    const chatCookie = useCookie<boolean>('chatCookie')

    const { isMobileView } = usePlaygameView()
    const { isBingo } = useGameType()

    const state = useState('playgame', () => ({
        sound: soundCookie.value,
        fullScreen: false,
        chat: isMobileView.value ? false : chatCookie.value,
        chatMinimized: false,
        paymentsVisible: false,
        menuVisible: false,
        balanceDropdownVisible: false,
        bingoSidegameVisible: false,
        bingoSidegamePlaying: false,
        bingoStatusHidden: false,
        canLoadChat: false,
        gameHasFreeSpins: false,
        gameStarting: false,

        // Games
        focusedGame: '',
        games: new Map<string, Game>()
    }))

    const getGame = (identifier: string) => state.value.games.get(identifier)

    const removeGameFromState = (identifier: string) => {
        if (getGame(identifier)) {
            state.value.games.delete(identifier)
        }
    }

    const isDialogOpen = computed<boolean>(() => state.value.paymentsVisible || state.value.menuVisible)
    const message = useState<PlaygameMessage | null>('playgame-message', () => null)
    const gamesArray = computed(() => Array.from(state.value.games.values()))
    const getFocusedGame = computed(() => state.value.focusedGame ? state.value.games.get(state.value.focusedGame) : null)

    const getEvolutionGames = computed(() =>
        Array.from(state.value.games.values()).filter(game => ProviderService.isEvolution(game.providerId))
    )
    const getGamesGlobalGames = computed(() =>
        Array.from(state.value.games.values()).filter(game => ProviderService.isGamesGlobal(game.providerId))
    )
    const getMagnetGames = computed(() =>
        Array.from(state.value.games.values()).filter(game => ProviderService.isMagnet(game.providerId))
    )
    const getBingoGames = computed(() =>
        Array.from(state.value.games.values()).filter(game => isBingo(game.game_type))
    )
    const getPariplayGames = computed(() =>
        Array.from(state.value.games.values()).filter(game => ProviderService.isPariplay(game.providerId))
    )

    const isPlayingEvolutionGames = computed<boolean>(() => getEvolutionGames.value.length > 0)
    const isPlayingGamesGlobalGames = computed<boolean>(() => getGamesGlobalGames.value.length > 0)
    const isPlayingBingo = computed<boolean>(() => getBingoGames.value.length > 0)
    const isPlayingPariplayGames = computed<boolean>(() => getPariplayGames.value.length > 0)

    const isPlayingGameAlready = (slug: string): boolean => {
        return gamesArray.value.some(game => game.combinedGame.slug === slug)
    }

    const isOneAtATimeRestricted = (gameType: string | undefined, provider: number | GameProviderSlug, slug?: string): boolean => {
        if (isBingo(gameType)) {
            return slug ? isPlayingGameAlready(slug) : false
        }

        return (ProviderService.isEvolution(provider) && isPlayingEvolutionGames.value)
            || (ProviderService.isGamesGlobal(provider) && isPlayingGamesGlobalGames.value)
            || (ProviderService.isPariplay(provider) && isPlayingPariplayGames.value)
    }

    return {
        state,
        message,
        gamesArray,
        soundCookie,
        chatCookie,
        getGame,
        removeGameFromState,
        getFocusedGame,
        getEvolutionGames,
        getGamesGlobalGames,
        getMagnetGames,
        isPlayingEvolutionGames,
        isPlayingGamesGlobalGames,
        isPlayingPariplayGames,
        isPlayingBingo,
        isDialogOpen,
        isPlayingGameAlready,
        isOneAtATimeRestricted
    }
})
